import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { Button } from "@babylonjs/gui/2D/controls/button";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { ScrollViewer } from "@babylonjs/gui/2D/controls/scrollViewers/scrollViewer";
import { Control } from "@babylonjs/gui/2D/controls/control";
import type { PanelDef, QuizDef } from "./schema";
import { t } from "./i18n";

const OCEAN = "#0e2233";
const GOLD = "#d8b24a";

function label(text: string, size: number, color: string, wrap = true): TextBlock {
  const tb = new TextBlock();
  tb.text = text;
  tb.color = color;
  tb.fontSize = size;
  tb.textWrapping = wrap;
  tb.resizeToFit = true;
  tb.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
  tb.paddingBottom = "6px";
  return tb;
}

/** PanneauBiblique — full-screen pedagogical card for a biblical passage. */
export function openPanel(adt: AdvancedDynamicTexture, panel: PanelDef, onClose?: () => void): void {
  const scrim = new Rectangle("panelScrim");
  scrim.background = "rgba(3,8,14,0.72)";
  scrim.thickness = 0;
  scrim.width = 1;
  scrim.height = 1;
  scrim.zIndex = 50;
  adt.addControl(scrim);

  const card = new Rectangle("panelCard");
  card.width = "560px";
  card.heightInPixels = Math.min(620, adt.getSize().height - 60);
  card.cornerRadius = 18;
  card.thickness = 2;
  card.color = GOLD;
  card.background = OCEAN;
  scrim.addControl(card);

  const scroll = new ScrollViewer();
  scroll.thickness = 0;
  scroll.width = 1;
  scroll.height = 1;
  scroll.barColor = GOLD;
  scroll.color = "transparent";
  card.addControl(scroll);

  const stack = new StackPanel();
  stack.paddingLeft = "26px";
  stack.paddingRight = "26px";
  stack.paddingTop = "22px";
  stack.paddingBottom = "22px";
  scroll.addControl(stack);

  stack.addControl(label(t(panel.title), 26, GOLD));
  stack.addControl(label(panel.reference, 16, "#9fc3d8"));
  const verse = label("« " + t(panel.passage) + " »", 20, "#f2ede0");
  verse.fontStyle = "italic";
  verse.paddingTop = "8px";
  verse.paddingBottom = "12px";
  stack.addControl(verse);
  stack.addControl(label(t(panel.explain), 17, "#dfe7ee"));
  if (panel.didYouKnow) {
    stack.addControl(label("💡 " + t({ fr: "Le savais-tu ? ", en: "Did you know? " }) + t(panel.didYouKnow), 15, "#c8b06a"));
  }
  if (panel.application) {
    stack.addControl(label("🕊 " + t(panel.application), 16, "#a9d6c0"));
  }

  const row = new StackPanel();
  row.isVertical = false;
  row.height = "56px";
  row.paddingTop = "14px";
  stack.addControl(row);

  const grave = Button.CreateSimpleButton("grave", "✧ " + t({ fr: "Graver ce verset", en: "Grave this verse" }));
  grave.width = "220px";
  grave.height = "42px";
  grave.color = OCEAN;
  grave.background = GOLD;
  grave.cornerRadius = 10;
  grave.paddingRight = "10px";
  grave.onPointerUpObservable.add(() => {
    try {
      const key = "kq_treasure";
      const arr: string[] = JSON.parse(localStorage.getItem(key) || "[]");
      if (!arr.includes(panel.reference)) arr.push(panel.reference);
      localStorage.setItem(key, JSON.stringify(arr));
    } catch {
      /* ignore */
    }
    grave.textBlock!.text = "✓ " + t({ fr: "Gravé au cœur", en: "Graved in the heart" });
  });
  row.addControl(grave);

  const close = Button.CreateSimpleButton("close", t({ fr: "Fermer", en: "Close" }));
  close.width = "120px";
  close.height = "42px";
  close.color = GOLD;
  close.background = "transparent";
  close.cornerRadius = 10;
  close.onPointerUpObservable.add(() => {
    adt.removeControl(scrim);
    onClose?.();
  });
  row.addControl(close);
}

/** A quick quiz card; explanation always shown after answering (grace framing). */
export function openQuiz(adt: AdvancedDynamicTexture, quiz: QuizDef, onDone?: (correct: boolean) => void): void {
  const scrim = new Rectangle("quizScrim");
  scrim.background = "rgba(3,8,14,0.72)";
  scrim.thickness = 0;
  scrim.zIndex = 50;
  adt.addControl(scrim);

  const card = new Rectangle("quizCard");
  card.width = "520px";
  card.adaptHeightToChildren = true;
  card.cornerRadius = 18;
  card.thickness = 2;
  card.color = GOLD;
  card.background = OCEAN;
  scrim.addControl(card);

  const stack = new StackPanel();
  stack.paddingLeft = "24px";
  stack.paddingRight = "24px";
  stack.paddingTop = "20px";
  stack.paddingBottom = "20px";
  card.addControl(stack);

  stack.addControl(label(t(quiz.prompt), 21, GOLD));

  quiz.choices.forEach((choice, i) => {
    const b = Button.CreateSimpleButton("q" + i, t(choice));
    b.height = "44px";
    b.paddingTop = "8px";
    b.color = "#f2ede0";
    b.background = "#163247";
    b.cornerRadius = 10;
    b.onPointerUpObservable.add(() => {
      const correct = i === quiz.answerIndex;
      b.background = correct ? "#2c6b3f" : "#7a3b3b";
      stack.addControl(label((correct ? "✓ " : "✕ ") + t(quiz.explain), 15, "#dfe7ee"));
      const cont = Button.CreateSimpleButton("cont", t({ fr: "Continuer", en: "Continue" }));
      cont.height = "40px";
      cont.paddingTop = "10px";
      cont.color = OCEAN;
      cont.background = GOLD;
      cont.cornerRadius = 10;
      cont.onPointerUpObservable.add(() => {
        adt.removeControl(scrim);
        onDone?.(correct);
      });
      stack.addControl(cont);
    });
    stack.addControl(b);
  });
}
